import {BaseElement, BaseElementAsync, MsgLabel, OSkinableInit} from "/@back@/commons/basis.js";
import {IApp, IAppCtx} from "/@back@/core/appFrame.js";
import {JItemEdAppDef} from "/@back@/wsp/plugins/itemEdPlg.js";
import {ItemEditInDialogInfoBroker, ItemXmlEd, OItemXmlEdInit} from "/@back@/wsp/views/item/itemXmlEd.js";
import {IReg, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {IView, VIEWS} from "/@lib@/commons/views.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {IChainEnv} from "/@lib@/wsp/chain.js";
import {AppHeader} from "/@back@/core/widgets/appHeader.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {Desk} from "/@lib@/commons/desk.js";
import {SRC} from "/@lib@/wsp/src.js";
import {JWspUriChangeMsg, WSP} from "/@lib@/wsp/wsp.js";
import {EWspChangesEvts, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";

/** App affichant un éditeur d'un item. */
export interface ItemEdApp extends BaseElement, IView {
	initialize(init: IAppCtx<IChainEnv> & OSkinableInit): this;
}

export class ItemEdApp extends BaseElementAsync implements IApp<IChainEnv> {

	appDef: JItemEdAppDef;

	reg: IReg<IChainEnv & IUiEnv>;

	itemEditor: ItemXmlEd;

	protected async _initialize(init: IAppCtx<IChainEnv> & OItemXmlEdInit) {
		this.reg = this.findReg(init);
		this.appDef = init.appDef as JItemEdAppDef;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		if (!Desk.checkCompat('edit')) {
			const msg = sr.appendChild(<div>
				<p>Ce navigateur n'est pas compatible avec l'édition de texte riche.</p>
				<p>Vous pouvez utiliser un des navigateurs à jour suivants :</p>
				<ul>
					{Desk.knownNavCompat.map((n) => n.url ? <li><a href={n.url} target="_blank">{n.name}</a></li> : <li>{n.name}</li>)}
				</ul>
			</div>);
			return;
		}

		const root = sr.appendChild(<div style="display:contents"/>);

		//AppHeader (après le itemEditor car on le référence dans le appHeader)
		sr.insertBefore(new AppHeader<ItemEdApp>().initialize({
			reg: this.reg,
			mainActions: this.reg.getList("actions:itemEdApp:bar:main"),
			actionContext: this,
			focusListening: root
		}), root);
		return this.initEditor(root, init);
	}

	async initEditor(root: HTMLElement, init: IAppCtx<IChainEnv> & OItemXmlEdInit) {
		try {
			const subInit = Object.create(init) as OItemXmlEdInit;
			const def = this.appDef.itemEd;
			subInit.reg = this.reg;
			subInit.wspCd = def.wspCd;
			subInit.srcRef = def.srcRef;
			subInit.editorKey = def.editorKey;
			subInit.initItemReg = (reg) => {
				reg.env.infoBroker = new ItemEditInDialogInfoBroker(WSP.findWspReg(reg));
			};
			this.itemEditor = root.appendChild(new ItemXmlEd().initialize(subInit));
			await this.itemEditor.initializedAsync;
			const currentModel = this.itemEditor.reg.env.longDesc.itModel;
			this.itemEditor.reg.env.place.eventsMgr.on("wspUriChange", (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => {
				if (msg.wspCd !== this.itemEditor.reg.env.wsp.code) return;
				if (msg.type === EWspChangesEvts.r && SRC.isSubUriOrEqual(msg.srcUri, this.itemEditor.reg.env.longDesc.srcUri)) {
					if (this.itemEditor) this.showFailure("Cet item a été supprimé.");
				} else if (msg.type === EWspChangesEvts.u && msg.srcUri === this.itemEditor.reg.env.longDesc.srcUri) {
					//check chgt de model
					this.itemEditor.reg.env.wsp.fetchShortDesc(SRC.srcRef(msg)).then((sd) => {
						if (currentModel !== sd.itModel && this.itemEditor) {
							const itemType = this.itemEditor.reg.env.wsp.wspMetaUi.getItemType(sd.itModel);
							if (itemType && itemType.getEditor(subInit.editorKey)) {
								this.itemEditor.closeEditor();
								this.itemEditor.remove();
								this.itemEditor = null;
								return this.initEditor(root, init);
							} else {
								this.showFailure("Une modification externe de cet item ne permet plus son affichage dans ce contexte.");
							}
						}
					}).catch(() => {
						this.showFailure("Échec à l'affichage de cet item suite à une modification externe.");
					});
				}
			})
		} catch (e) {
			this.showFailure("Échec à l'affichage de cet item.");
		}
	}

	showFailure(msg: string) {
		if (this.itemEditor) this.itemEditor.remove();
		this.itemEditor = null;
		this.shadowRoot.appendChild(new MsgLabel().initialize({
			reg: this.reg,
			label: msg
		}));
	}

	onViewHidden(closed?: boolean) {
		if (this.itemEditor) {
			if (closed) {
				this.itemEditor.closeEditor();
			} else {
				VIEWS.onViewHidden(this.itemEditor, closed);
			}
		}
	}

	updateAppDef(def: JItemEdAppDef): boolean {
		return false;
	}
}

REG.reg.registerSkin('wsp-item-ed-app', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
	  min-width: 0;
	  flex-direction: column;
  }

  wsp-item-ed-app {
	  flex: 1;
  }

  hr {
	  border-top: 1px solid var(--border-color);
	  border-inline-start: 1px solid var(--border-color);
	  border-inline-end: none;
	  border-bottom: none;
	  margin: 0;
  }
`);

customElements.define('wsp-item-ed-app', ItemEdApp);
